import { Gift } from 'lucide-react'
import type { StampLotterySummary, StampPrizeSummary } from '../services/api'
import { useI18n } from '../i18n/useI18n'

/**
 * 特典グッズの在庫表示 (#1126)。「残りわずか」「本日分終了」の判定はサーバ返却の
 * status をそのまま使う — 閾値ロジックをフロントに複製しない。
 *
 * #1871 抽選をやっているイベントでは、**同じカードの中**に並べる。外に小さな
 * 文字で置くと、何の数字なのか宙に浮いて見える。景品の話は 1 箇所にまとめる。
 */
export default function PrizeStockBadge({
  prize,
  lottery,
}: {
  prize: StampPrizeSummary
  lottery?: StampLotterySummary | null
}) {
  const { t } = useI18n()
  const soldOut = prize.status === 'sold_out'
  const low = prize.status === 'low'
  return (
    <div className={`rounded-2xl p-4 ${soldOut ? 'bg-gray-100' : 'bg-gold/10'}`}>
      <div className="flex items-center gap-3">
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
          soldOut ? 'bg-gray-200 text-gray-400' : 'bg-gold/20 text-gold'
        }`}
      >
        <Gift size={22} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-body text-text-tertiary">{t('stampRally.prizeLabel')}</p>
        <p className="text-sm font-body font-medium text-text-primary truncate">{prize.name}</p>
        <p className={`text-xs font-body mt-0.5 ${soldOut ? 'text-text-tertiary' : 'text-text-secondary'}`}>
          {t('stampRally.stockRemaining', {
            remaining: String(prize.remaining),
            total: String(prize.totalStock),
          })}
        </p>
      </div>
      {soldOut && (
        <span className="shrink-0 text-xs font-body font-medium bg-gray-200 text-text-secondary rounded-full px-3 py-1">
          {t('stampRally.stockSoldOut')}
        </span>
      )}
      {low && !soldOut && (
        <span className="shrink-0 text-xs font-body font-medium bg-yokoso-red/10 text-yokoso-red rounded-full px-3 py-1">
          {t('stampRally.stockLow')}
        </span>
      )}
      </div>

      {/* #1871 抽選。**完走報酬とは別の景品・別の在庫**なので、区切って名前も出す。
        * 同じカードに入れるのは「景品の話」を 1 箇所にまとめるためで、
        * 同じ景品という意味ではない。 */}
      {lottery && (
        <div className="mt-3 border-t border-black/10 pt-3">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 rounded-full bg-navy/10 px-2 py-0.5 font-body text-[10px] font-medium text-navy">
              {t('stampRally.lotteryTitle')}
            </span>
            <p className="min-w-0 truncate font-body text-sm font-medium text-text-primary">
              {lottery.name}
            </p>
          </div>
          {/* **残数と当選率を 1 行に並べない。**
            *
            * 当選率の分母は「くじ全体の残り本数（＝想定参加者数）」で、
            * 前半の「当たり N 本中 残り M 本」とは**別のもの**。並べると
            * 誰でも M/N を計算してしまい、「10本中 残り10本なのに 5%」と
            * 意味が通らなくなる（実際にそう指摘を受けた）。
            *
            * **くじの枚数を先に出す。** 残りの当たり ÷ 残りのくじ が当選率
            * なので、枚数が見えれば自分で確かめられる。実物のくじ箱と同じ
            * 見え方になるので説明も要らない。 */}
          {lottery.remaining > 0 ? (
            <>
              <p className="mt-0.5 font-body text-xs text-text-secondary">
                {t('stampRally.lotteryBox', {
                  drawTotal: String(lottery.drawTotal),
                  remainingDraws: String(lottery.remainingDraws),
                })}
              </p>
              <p className="mt-0.5 font-body text-xs text-text-secondary">
                {t('stampRally.lotteryStock', {
                  total: String(lottery.winCount),
                  remaining: String(lottery.remaining),
                })}
              </p>
              {lottery.oddsPercent > 0 && (
                <p className="mt-0.5 font-body text-xs text-text-tertiary">
                  {lottery.oddsPercent >= 100
                    ? t('stampRally.lotteryOddsAll')
                    : t('stampRally.lotteryOdds', { odds: String(lottery.oddsPercent) })}
                </p>
              )}
            </>
          ) : (
            <p className="mt-0.5 font-body text-xs text-text-secondary">
              {t('stampRally.lotterySoldOut')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
